// Results-screen damage report (owner 2026-11-03): per-source damage bars, shares and DPS. The row maths is pure
// (no DOM, no sim import) so it is unit-tested; `damageReportHtml` renders it with the same names/icons as the pause build.
import { PASSIVES, WEAPONS, type PassiveId, type WeaponId } from '../core/content';
import { FUSIONS, isFusionId } from '../core/fusions';
import type { Sim } from '../core/sim';
import { fusionIcon, techIcon, traitIcon } from './icons';

export interface DamageRow {
  id: string;
  dmg: number;
  /** share of all damage, 0..100 */
  share: number;
  /** bar length relative to the top source, 0..100 */
  bar: number;
  /** damage per second over the time the source was owned (first damage .. endTime) */
  dps: number;
}

export interface DamageReport { rows: DamageRow[]; total: number; dps: number }

/** Compact number: 950, 1.2K, 340K, 1.2M, 3.4B. */
export function compactNum(n: number): string {
  const a = Math.abs(n);
  const f = (v: number, u: string) => `${v >= 100 ? Math.round(v) : Math.round(v * 10) / 10}${u}`;
  if (a < 1000) return String(Math.round(n));
  if (a < 999_500) return f(n / 1e3, 'K');
  if (a < 999_500_000) return f(n / 1e6, 'M');
  return f(n / 1e9, 'B');
}

/** Sorted (highest first) rows; zero-damage sources are dropped. Shorter than 1 s of ownership counts as 1 s for DPS. */
export function buildDamageRows(dmgBySrc: Record<string, number>, dmgSince: Record<string, number>, endTime: number): DamageRow[] {
  const ents = Object.entries(dmgBySrc).filter(([, v]) => v > 0);
  const total = ents.reduce((a, [, v]) => a + v, 0);
  ents.sort((a, b) => b[1] - a[1] || (a[0] < b[0] ? -1 : 1));
  const top = ents.length ? ents[0][1] : 1;
  return ents.map(([id, dmg]) => ({
    id, dmg,
    share: (dmg / total) * 100,
    bar: (dmg / top) * 100,
    dps: dmg / Math.max(1, endTime - (dmgSince[id] ?? 0)),
  }));
}

export function buildDamageReport(sim: Pick<Sim, 'dmgBySrc' | 'dmgSince' | 'time'>): DamageReport {
  const rows = buildDamageRows(sim.dmgBySrc, sim.dmgSince, sim.time);
  const total = rows.reduce((a, r) => a + r.dmg, 0);
  return { rows, total, dps: total / Math.max(1, sim.time) };
}

/** Damage-over-time samples (every 10 s of sim time), kept in the app layer so the core stays lean. */
export class DamageSampler {
  static readonly EVERY = 10;
  static readonly CAP = 120; // 20 minutes
  private sim: object | null = null;
  private next = DamageSampler.EVERY;
  samples: { t: number; d: Record<string, number> }[] = [];
  sample(sim: Pick<Sim, 'dmgBySrc' | 'time'>): void {
    if (this.sim !== sim || sim.time < this.next - DamageSampler.EVERY) { this.sim = sim; this.samples = []; this.next = DamageSampler.EVERY; } // new run
    if (sim.time < this.next) return;
    this.next = Math.floor(sim.time / DamageSampler.EVERY) * DamageSampler.EVERY + DamageSampler.EVERY;
    const d: Record<string, number> = {};
    for (const k in sim.dmgBySrc) d[k] = sim.dmgBySrc[k];
    this.samples.push({ t: sim.time, d });
    if (this.samples.length > DamageSampler.CAP) this.samples.shift();
  }
  /** cumulative damage of one source at each sample, ending with the final value */
  series(id: string, finalDmg: number): number[] {
    return [0, ...this.samples.map((s) => s.d[id] ?? 0), finalDmg];
  }
}

const esc = (s: string) => s.replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' })[c]!);

function label(sim: Sim, id: string): { name: string; icon: string } {
  if (isFusionId(id)) return { name: `✦ ${FUSIONS[id].name}`, icon: fusionIcon(id) };
  if (id in WEAPONS) { const w = WEAPONS[id as WeaponId], evo = sim.evolved.has(id as WeaponId); return { name: evo ? w.evolvedName : w.name, icon: techIcon(id as WeaponId, evo) }; }
  if (id in PASSIVES) return { name: PASSIVES[id as PassiveId].name, icon: traitIcon(id as PassiveId) };
  const name = id.replace(/[_-]+/g, ' ').replace(/^./, (c) => c.toUpperCase());
  return { name, icon: '<span class="dr-dot">✦</span>' };
}

function spark(vals: number[]): string {
  const w = 60, h = 16, max = vals[vals.length - 1] || 1;
  const pts = vals.map((v, i) => `${((i / Math.max(1, vals.length - 1)) * w).toFixed(1)},${(h - 1 - (v / max) * (h - 2)).toFixed(1)}`).join(' ');
  return `<svg class="dr-spark" viewBox="0 0 ${w} ${h}" aria-hidden="true"><polyline points="${pts}" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>`;
}

export const DR_TOP = 8;

/** Whole section: heading, total line, rows (rows past the top 8 are hidden until `#over-dmg.all`). */
export function damageReportHtml(sim: Sim, sampler?: DamageSampler): string {
  const rep = buildDamageReport(sim);
  if (!rep.rows.length) return '';
  const rows = rep.rows.map((r, i) => {
    const l = label(sim, r.id);
    const sp = sampler && i < 3 && sampler.samples.length >= 2 ? spark(sampler.series(r.id, r.dmg)) : '';
    return `<div class="dr-row${i === 0 ? ' top' : ''}${i >= DR_TOP ? ' extra' : ''}">`
      + `<span class="dr-ico">${l.icon}</span>`
      + `<div class="dr-main"><div class="dr-h"><b>${esc(l.name)}</b>${sp}<span class="dr-n">${compactNum(r.dmg)}</span><span class="dr-p">${r.share.toFixed(r.share < 10 ? 1 : 0)}%</span></div>`
      + `<div class="dr-track"><i style="width:${r.bar.toFixed(1)}%"></i></div>`
      + `<div class="dr-s">${compactNum(r.dps)} DPS</div></div></div>`;
  }).join('');
  return `<div class="dr-head">DAMAGE</div>`
    + `<div class="dr-total">Total <b>${compactNum(rep.total)}</b> · <b>${compactNum(rep.dps)}</b> DPS overall${rep.rows.length > 0 ? ` · Top: <b>${esc(label(sim, rep.rows[0].id).name)}</b>` : ''}</div>`
    + rows;
}
